// Mobile "Year in Review" — the season recap, redesigned from the old Basketball-Reference-style
// desktop Season Summary into a single-column, sectioned mobile screen (Summer League purple
// header, Spotify-style dark cards, teal accent). Data comes from the existing `history` worker
// view (awards, champ, best records, honor teams, retirees). Design: design_handoff_season_recap.

import { useState } from "react";
import useTitleBar from "../hooks/useTitleBar.tsx";
import { helpers } from "../util/helpers.ts";
import { useLocal } from "../util/local.ts";
import { AWARD_NAMES } from "../../common/constants.ts";
import type { View } from "../../common/types.ts";

// Display team colors, brightened for legibility on the dark background (from the design handoff).
const TC: Record<string, string> = {
	OKC: "#2f9fe0", ATL: "#ff5157", SA: "#b6b9bd", DEN: "#4f79b8", LAL: "#9a6cff", DET: "#ff5a63",
	TOR: "#ff5470", POR: "#ff6a63", CLE: "#c65a86", PHO: "#ff8a4a", ORL: "#2f9fe0", WAS: "#4f7fd6",
	BKN: "#a6adb4", UTA: "#a06cff", SAC: "#9a6cd0", GS: "#4f8fe0", HOU: "#ff5470", BOS: "#3fbf6f",
	MIN: "#5a97cc", LAC: "#4f8fe0", DAL: "#4f9fe0", PHI: "#4f8fe0", MIL: "#3fae7a", NYK: "#ff8a4a",
	IND: "#ffd24a", MEM: "#7f9fd6", NOP: "#b89a5a", CHA: "#3fbfae", CHI: "#ff5470", HoF: "#e0c24a",
};
const teamColor = (code?: string) => (code && TC[code]) || "#4A9EFF";

const Dot = ({ code, size, font }: { code: string; size: number; font: number }) => (
	<div
		className="rec-dot"
		style={{
			width: size,
			height: size,
			borderRadius: Math.round(size * 0.28),
			fontSize: font,
			background: teamColor(code),
		}}
	>
		{code}
	</div>
);

const Chip = ({ code }: { code?: string }) =>
	code ? (
		<span className="rec-chip" style={{ color: teamColor(code) }}>
			{code}
		</span>
	) : null;

// Award stat line: "29.1 pts · 4.6 reb · 6.4 ast" (defense awards use reb/blk/stl).
const statLine = (award: any, defense = false) => {
	if (!award) return "";
	const cols: [string, string][] = defense
		? [["trb", "reb"], ["blk", "blk"], ["stl", "stl"]]
		: [["pts", "pts"], ["trb", "reb"], ["ast", "ast"]];
	return cols
		.map(([key, label]) => `${helpers.roundStat(award[key], key)} ${label}`)
		.join(" · ");
};

const GmSeasonRecap = (props: View<"history">) => {
	const { season } = props;
	useTitleBar({ title: "Season Summary" });
	const { userTid } = useLocal(["userTid"]);

	const [honor, setHonor] = useState<"league" | "def" | "rookie">("league");
	const [retExpanded, setRetExpanded] = useState(false);
	const [champExpanded, setChampExpanded] = useState(false);

	if (props.invalidSeason) {
		return (
			<div className="rec">
				<style>{styles}</style>
				<div className="rec-title" style={{ padding: "24px 20px" }}>
					Invalid season.
				</div>
			</div>
		);
	}

	const { awards, champ, confs, retiredPlayers, retiredStat } = props as any;

	// season is the ENDING year (2027 = the 2026-27 season), so range is (season-1)–season.
	const seasonLabel = `${season - 1}–${String(season % 100).padStart(2, "0")}`;

	// Honor teams: allLeague/allDefensive are nested [{title, players}]; allRookie is a flat list.
	const honorTiers: { title: string; players: any[] }[] =
		honor === "rookie"
			? [{ title: AWARD_NAMES.allRookie ?? "All-Rookie Team", players: awards.allRookie ?? [] }]
			: (honor === "league" ? awards.allLeague : awards.allDefensive) ?? [];

	const bestRecords = (awards.bestRecordConfs ?? []).filter(Boolean);

	const retiredFull = retiredPlayers ?? [];
	const retiredShown = retExpanded ? retiredFull : retiredFull.slice(0, 12);
	const retiredStatLabel = String(retiredStat).toUpperCase();

	const mine = (tid: number | undefined) => tid === userTid;

	return (
		<div className="rec">
			<style>{styles}</style>
			<div className="rec-grad" />

			<div className="rec-body">
				{/* Title */}
				<div className="rec-titlewrap">
					<div className="rec-eyebrow">{seasonLabel} · SUMMER LEAGUE</div>
					<div className="rec-title">
						The year
						<br />
						in review.
					</div>
				</div>

				{/* Champions */}
				{champ ? (
					<div className={`rec-champ ${mine(champ.tid) ? "rec-mine" : ""}`}>
						<div className="rec-champ-glow" />
						<div className="rec-overline">LEAGUE CHAMPIONS</div>
						<div className="rec-champ-team">
							<Dot code={champ.seasonAttrs.abbrev} size={44} font={13} />
							<div className="rec-champ-name">
								{champ.seasonAttrs.region} {champ.seasonAttrs.name}
							</div>
						</div>
						{awards.finalsMvp ? (
							<div className="rec-champ-mvp">
								<div className="rec-sublabel">FINALS MVP</div>
								<div className="rec-champ-mvprow">
									<span className="rec-mvpname">{awards.finalsMvp.name}</span>
									<Chip code={awards.finalsMvp.abbrev} />
								</div>
								<div className="rec-line">{statLine(awards.finalsMvp)}</div>
							</div>
						) : null}

						{champ.seasonLine ? (
							<>
								<button
									className="rec-champ-toggle"
									onClick={() => setChampExpanded((v) => !v)}
								>
									{champExpanded ? "Hide season ▴" : "Season ratings & stats ▾"}
								</button>
								{champExpanded ? (
									<div className="rec-champ-season">
										{champ.seasonLine.ovr !== undefined ? (
											<div className="rec-champ-ovr">
												<span className="rec-champ-ovr-v">
													{champ.seasonLine.ovr}
												</span>
												<span className="rec-champ-ovr-l">TEAM OVR</span>
											</div>
										) : null}
										{(
											[
												["Regular Season", champ.seasonLine.reg],
												["Playoffs", champ.seasonLine.po],
											] as const
										).map(([label, s]) => (
											<div key={label} className="rec-champ-split">
												<div className="rec-champ-split-h">{label}</div>
												<div className="rec-champ-split-grid">
													<div className="rec-champ-stat">
														<span className="rec-champ-stat-v">
															{s.won}–{s.lost}
														</span>
														<span className="rec-champ-stat-l">RECORD</span>
													</div>
													<div className="rec-champ-stat">
														<span className="rec-champ-stat-v">
															{s.pts.toFixed(1)}
														</span>
														<span className="rec-champ-stat-l">PPG</span>
													</div>
													<div className="rec-champ-stat">
														<span className="rec-champ-stat-v">
															{s.oppPts.toFixed(1)}
														</span>
														<span className="rec-champ-stat-l">OPP PPG</span>
													</div>
													<div className="rec-champ-stat">
														<span
															className="rec-champ-stat-v"
															style={{
																color:
																	s.pts - s.oppPts >= 0 ? "#18E0A8" : "#E2504E",
															}}
														>
															{s.pts - s.oppPts >= 0 ? "+" : ""}
															{(s.pts - s.oppPts).toFixed(1)}
														</span>
														<span className="rec-champ-stat-l">MARGIN</span>
													</div>
												</div>
											</div>
										))}
									</div>
								) : null}
							</>
						) : null}
					</div>
				) : null}

				{/* Conference finals MVPs */}
				{awards.sfmvp && awards.sfmvp.length ? (
					<>
						<div className="rec-h">Conference finals MVPs</div>
						<div className="rec-stack">
							{awards.sfmvp.map((p: any, i: number) =>
								p ? (
									<div key={i} className={`rec-row ${mine(p.tid) ? "rec-mine" : ""}`}>
										<Dot code={p.abbrev} size={42} font={12} />
										<div className="rec-rowbody">
											<div className="rec-rowname">{p.name}</div>
											<div className="rec-line">{statLine(p)}</div>
										</div>
									</div>
								) : null,
							)}
						</div>
					</>
				) : null}

				{/* Best record */}
				{bestRecords.length ? (
					<>
						<div className="rec-h">Best record</div>
						<div className="rec-2col rec-pad">
							{bestRecords.map((t: any, i: number) => (
								<div key={t.tid} className={`rec-card ${mine(t.tid) ? "rec-mine" : ""}`}>
									<div className="rec-overline">
										{(confs[i]?.name ?? "").toUpperCase()}
									</div>
									<div className="rec-recrow">
										<Dot code={t.abbrev} size={26} font={9.5} />
										<div className="rec-rec">{helpers.formatRecord(t)}</div>
									</div>
									<div className="rec-recteam">
										{t.region} {t.name}
									</div>
								</div>
							))}
						</div>
					</>
				) : null}

				{/* Individual awards */}
				<div className="rec-h">Individual awards</div>
				{awards.mvp ? (
					<div className={`rec-mvpcard ${mine(awards.mvp.tid) ? "rec-mine" : ""}`}>
						<div className="rec-overline">
							{(AWARD_NAMES.mvp ?? "Most Valuable Player").toUpperCase()}
						</div>
						<div className="rec-champ-mvprow" style={{ marginTop: 8 }}>
							<span className="rec-mvpname rec-mvpbig">{awards.mvp.name}</span>
							<Chip code={awards.mvp.abbrev} />
						</div>
						<div className="rec-line rec-line-mvp">{statLine(awards.mvp)}</div>
					</div>
				) : null}

				<div className="rec-2col rec-pad" style={{ marginTop: 10 }}>
					{[
						{ key: "dpoy", defense: true },
						{ key: "smoy", defense: false },
						{ key: "mip", defense: false },
						{ key: "roy", defense: false },
					].map(({ key, defense }) => {
						const a = awards[key];
						return (
							<div key={key} className={`rec-card ${a && mine(a.tid) ? "rec-mine" : ""}`}>
								<div className="rec-overline rec-awardtitle">
									{(AWARD_NAMES[key] ?? key).toUpperCase()}
								</div>
								{a ? (
									<>
										<div className="rec-awardrow">
											<span className="rec-awardname">{a.name}</span>
											<Chip code={a.abbrev} />
										</div>
										<div className="rec-line">{statLine(a, defense)}</div>
									</>
								) : (
									<div className="rec-line">—</div>
								)}
							</div>
						);
					})}
				</div>

				{/* Honors */}
				<div className="rec-h">Honors</div>
				<div className="rec-seg">
					{(
						[
							["league", "All-League"],
							["def", "All-Defensive"],
							["rookie", "All-Rookie"],
						] as const
					).map(([k, label]) => (
						<button
							key={k}
							className={`rec-seg-btn ${honor === k ? "active" : ""}`}
							onClick={() => setHonor(k)}
						>
							{label}
						</button>
					))}
				</div>

				<div className="rec-tiers">
					{honorTiers.map((tier) =>
						tier.players && tier.players.length ? (
							<div key={tier.title}>
								<div className="rec-tier-label">{tier.title}</div>
								<div className="rec-stack">
									{tier.players.map((p: any, i: number) =>
										p ? (
											<div
												key={p.pid}
												className={`rec-honor ${mine(p.tid) ? "rec-mine" : ""}`}
											>
												<span className="rec-honor-rank">{i + 1}</span>
												<span className="rec-honor-name">{p.name}</span>
												<Chip code={p.abbrev} />
											</div>
										) : null,
									)}
								</div>
							</div>
						) : null,
					)}
				</div>

				{/* Retired players */}
				<div className="rec-rethead">
					<div className="rec-h rec-h-inline">Retired players</div>
					<div className="rec-retcount">{retiredFull.length} this year</div>
				</div>
				<div className="rec-retsub">Sorted by career {retiredStatLabel}.</div>
				<div className="rec-2col rec-pad rec-retgrid">
					{retiredShown.map((p: any) => (
						<div key={p.pid} className="rec-retcard">
							<div className="rec-retrow">
								<span
									className="rec-retdot"
									style={{ background: teamColor(p.t?.abbrev ?? "HoF") }}
								/>
								<span className="rec-retname">{p.name}</span>
							</div>
							<div className="rec-retmeta">
								{p.t?.abbrev ? `${p.t.abbrev} · ` : ""}
								{p.age} yo · {helpers.roundStat(p.stat, retiredStat, true)}{" "}
								{retiredStatLabel}
								{p.hof ? " · HoF" : ""}
							</div>
						</div>
					))}
				</div>
				{retiredFull.length > 12 ? (
					<button
						className="rec-expand"
						onClick={() => setRetExpanded((v) => !v)}
					>
						{retExpanded
							? "Show fewer ▴"
							: `Show all ${retiredFull.length} retirees ▾`}
					</button>
				) : null}
			</div>
		</div>
	);
};

const styles = `
.rec { position:relative; max-width:480px; margin:0 auto; color:#fff;
  font-family:Montserrat,system-ui,sans-serif; }
.rec-grad { position:absolute; top:0; left:0; right:0; height:230px; pointer-events:none;
  background:linear-gradient(180deg,#3a1550 0%,#4a1c46 34%,#1e1630 66%,#121212 100%); }
.rec-body { position:relative; }
.rec-titlewrap { padding:14px 20px 4px; }
.rec-eyebrow { font-size:11px; font-weight:800; letter-spacing:1.6px; color:#18E0A8; }
.rec-title { font-size:33px; font-weight:900; letter-spacing:-1.3px; line-height:1.03; margin-top:9px; }
.rec-dot { flex:none; display:flex; align-items:center; justify-content:center; font-weight:900;
  letter-spacing:.3px; color:#0b0f13; }
.rec-chip { flex:none; padding:2px 6px; border-radius:6px; font-size:9px; font-weight:900;
  letter-spacing:.4px; background:rgba(255,255,255,.07); }
.rec-overline { font-size:10.5px; font-weight:900; letter-spacing:2px; color:#18E0A8; }
.rec-h { padding:22px 16px 2px; font-size:16px; font-weight:900; letter-spacing:-.3px; }
.rec-line { font-size:11.5px; color:#9fb0bc; font-weight:600; margin-top:2px; }
.rec-mine { outline:1.5px solid rgba(24,224,168,.55); outline-offset:-1px; }

.rec-champ { margin:16px 16px 0; border-radius:18px; padding:18px; position:relative; overflow:hidden;
  background:linear-gradient(150deg,#26314a 0%,#1a1f30 60%,#181818 100%); border:1px solid #2c3550; }
.rec-champ-glow { position:absolute; right:-20px; top:-20px; width:120px; height:120px; border-radius:50%;
  background:radial-gradient(circle,rgba(24,224,168,.22),transparent 70%); }
.rec-champ-team { display:flex; align-items:center; gap:12px; margin-top:12px; }
.rec-champ-name { font-size:22px; font-weight:900; letter-spacing:-.5px; line-height:1.05; }
.rec-champ-mvp { margin-top:15px; padding-top:14px; border-top:1px solid rgba(255,255,255,.1); }
.rec-sublabel { font-size:9px; font-weight:800; letter-spacing:1.2px; color:#9fb0bc; }
.rec-champ-mvprow { display:flex; align-items:center; gap:7px; margin-top:5px; }
.rec-champ-toggle { display:block; width:100%; margin-top:14px; padding:9px; border-radius:10px;
  border:1px solid rgba(255,255,255,.16); background:rgba(255,255,255,.06); color:#fff;
  font-family:inherit; font-weight:800; font-size:12px; letter-spacing:.3px; cursor:pointer; }
.rec-champ-season { margin-top:12px; display:flex; flex-direction:column; gap:12px; }
.rec-champ-ovr { display:flex; align-items:baseline; gap:8px; }
.rec-champ-ovr-v { font-size:26px; font-weight:900; letter-spacing:-.5px; }
.rec-champ-ovr-l { font-size:10px; font-weight:800; letter-spacing:.6px; color:rgba(255,255,255,.55); }
.rec-champ-split-h { font-size:11px; font-weight:800; letter-spacing:.6px; color:rgba(255,255,255,.6);
  margin-bottom:7px; }
.rec-champ-split-grid { display:flex; gap:14px; }
.rec-champ-stat { display:flex; flex-direction:column; }
.rec-champ-stat-v { font-size:16px; font-weight:900; letter-spacing:-.3px; }
.rec-champ-stat-l { font-size:9px; font-weight:800; letter-spacing:.4px; color:rgba(255,255,255,.5); }
.rec-mvpname { font-size:15px; font-weight:800; }
.rec-mvpbig { font-size:19px; font-weight:900; letter-spacing:-.4px; }

.rec-stack { padding:8px 16px 0; display:flex; flex-direction:column; gap:9px; }
.rec-row { display:flex; align-items:center; gap:11px; background:#181818; border:1px solid #232323;
  border-radius:13px; padding:12px 13px; }
.rec-rowbody { flex:1; min-width:0; }
.rec-rowname { font-size:13.5px; font-weight:800; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }

.rec-2col { display:grid; grid-template-columns:minmax(0,1fr) minmax(0,1fr); gap:10px; }
.rec-pad { padding:8px 16px 0; }
.rec-card { background:#181818; border:1px solid #232323; border-radius:13px; padding:13px; }
.rec-recrow { display:flex; align-items:center; gap:7px; margin-top:8px; }
.rec-rec { font-size:18px; font-weight:900; letter-spacing:-.4px; }
.rec-recteam { font-size:11.5px; color:#c9c9c9; font-weight:700; margin-top:6px; white-space:nowrap;
  overflow:hidden; text-overflow:ellipsis; }

.rec-mvpcard { margin:8px 16px 0; border-radius:14px; padding:14px 15px; border:1px solid #244a30;
  background:linear-gradient(150deg,#1d3524,#181818 62%); }
.rec-line-mvp { color:#a9c9b5; }
.rec-awardtitle { font-size:8.5px; letter-spacing:.7px; line-height:1.25; min-height:22px; }
.rec-awardrow { display:flex; align-items:center; gap:6px; margin-top:7px; }
.rec-awardname { font-size:14px; font-weight:800; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }

.rec-seg { display:flex; gap:5px; background:#1c1c1c; border-radius:11px; padding:4px; margin:8px 16px 0; }
.rec-seg-btn { flex:1; text-align:center; font-size:11px; font-weight:800; padding:8px 2px; border:none;
  border-radius:8px; cursor:pointer; white-space:nowrap; font-family:inherit; background:transparent; color:#cfcfcf; }
.rec-seg-btn.active { background:#18E0A8; color:#053a2c; }
.rec-tiers { padding:12px 16px 0; display:flex; flex-direction:column; gap:16px; }
.rec-tier-label { font-size:11px; font-weight:800; letter-spacing:.5px; color:#8fb3c9; margin-bottom:8px; }
.rec-honor { display:flex; align-items:center; gap:11px; background:#181818; border:1px solid #232323;
  border-radius:11px; padding:9px 12px; }
.rec-honor-rank { font-size:11px; font-weight:900; color:#5f6b73; width:14px; flex:none; }
.rec-honor-name { flex:1; font-size:13px; font-weight:700; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }

.rec-rethead { display:flex; align-items:baseline; justify-content:space-between; padding-right:16px; }
.rec-h-inline { padding:26px 16px 2px; }
.rec-retcount { font-size:11px; color:#8a8a8a; font-weight:700; }
.rec-retsub { font-size:11px; color:#7a7a7a; font-weight:600; padding:2px 16px 0; }
.rec-retgrid { gap:8px; }
.rec-retcard { background:#181818; border:1px solid #232323; border-radius:11px; padding:9px 11px; }
.rec-retrow { display:flex; align-items:center; gap:6px; }
.rec-retdot { width:7px; height:7px; border-radius:2px; flex:none; }
.rec-retname { font-size:12px; font-weight:800; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.rec-retmeta { font-size:10px; color:#8a8a8a; font-weight:700; margin-top:3px; }
.rec-expand { display:block; width:calc(100% - 32px); text-align:center; margin:16px; padding:12px;
  border-radius:24px; background:rgba(24,224,168,.1); border:1px solid rgba(24,224,168,.3); color:#18E0A8;
  font-size:12.5px; font-weight:800; cursor:pointer; font-family:inherit; }
`;

export default GmSeasonRecap;
